import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import type { LatLng } from '~/_shared/types/latLng';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import {
  getFormulaDemographicIds, isReportFormulaIsTooComplex,
} from '~/exportReport/template/reportTemplate.helpers';
import type { ReportTableColumn } from '~/exportReport/templateRenderer/exportReportTemplateRenderer.component';
import { createRadiusFilterItemRequest } from '~/spreadsheet/filter/radius/spreadsheetFilterRadius.factory';
import type {
  DemographicsQueryBulkRequestItem, DemographicsQueryBulkResults, DemographicsResult,
} from '~/store/demographics/demographics.repository';

export const normalizeReportDemographicId = (demographicId: string): string => demographicId.replace('%', '');

export const getReportTemplateDemographicIds = (template: ReportTemplate): ReadonlyArray<string> => {
  const demographicsWithFormula = new Set<string>();

  return [...template.items.reduce<Set<string>>((demographicIds, item) => {
    if (item.type !== 'table') {
      return demographicIds;
    }

    item.content.forEach(row => {
      if (row.type !== 'data-row') {
        return;
      }

      if (row.formula) {
        demographicsWithFormula.add(row.id);

        if (isReportFormulaIsTooComplex(row.formula)) {
          return;
        }

        getFormulaDemographicIds(row.formula.replace('%', '')).forEach(demographicId => {
          if (!demographicsWithFormula.has(demographicId)) {
            demographicIds.add(demographicId);
          }
        });

        return;
      }

      demographicIds.add(normalizeReportDemographicId(row.id));
    });

    return demographicIds;
  }, new Set())];
};

export const getReportDemographicDataFromResults = (
  data: DemographicsQueryBulkResults | null,
  columnId: string,
  demographicId: string
): DemographicsResult | null => {
  const columnData = data?.[columnId];
  if (!columnData) {
    return null;
  }

  return columnData.data.find(demographic => normalizeReportDemographicId(demographic.demographicId) === normalizeReportDemographicId(demographicId)) ?? null;
};

export const getReportDemographicLabelFromResults = (
  data: DemographicsQueryBulkResults | null,
  demographicId: string
): string | null => {
  if (!data) {
    return null;
  }

  for (const item of Object.values(data)) {
    if (!item.isSuccess) {
      continue;
    }

    const demographic = item.data.find(demographicData => normalizeReportDemographicId(demographicData.demographicId) === normalizeReportDemographicId(demographicId));
    if (demographic) {
      return demographic.name;
    }
  }

  return null;
};

export const createLocationExportReportDemographicsQueries = ({
  boundaryGroupId, center, demographicIds, mapId, tableColumns,
}: {
  boundaryGroupId: number | null;
  center: LatLng;
  demographicIds: ReadonlyArray<string>;
  mapId: number;
  tableColumns: ReadonlyArray<ReportTableColumn>;
}): DemographicsQueryBulkRequestItem[] => tableColumns.map(column => ({
  id: column.id,
  map_id: mapId,
  demographics_ids: demographicIds,
  ...(boundaryGroupId ? { boundary_group_id: boundaryGroupId } : {}),
  ...(column.id.startsWith('radius') ? {
    radius_filter: {
      type: 'or',
      radiuses: [createRadiusFilterItemRequest({
        radius: Number(column.id.replace('radius', '')),
        center,
        unit: UnitSystem.metric,
      })],
    },
  } : {}),
}));
